import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import type { FinanceCollectionsCenter } from '@/types/finance';
import { showApiError } from '@/utils/apiError';
import { usePermissions } from '@/hooks/usePermissions';

function fmt(n?: number) {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function FinanceCollectionsCenterPage() {
  const { hasAny } = usePermissions();
  const canRunDunning = hasAny('FINANCE_INVOICE_MANAGE', 'FINANCE_MANAGE');
  const [center, setCenter] = useState<FinanceCollectionsCenter | null>(null);
  const [running, setRunning] = useState(false);
  const queue = center?.queue ?? [];
  const { rows: paginatedQueue, tablePaginationProps: queuePaginationProps } = useClientPaginatedRows(queue);

  const load = () => financeApi.collectionsCenter().then(setCenter).catch((e) => showApiError(e, 'Failed to load collections'));

  useEffect(() => {
    load();
  }, []);

  const runDunning = () => {
    setRunning(true);
    financeApi
      .runCollectionsDunning()
      .then(() => load())
      .catch((e) => showApiError(e, 'Dunning run failed'))
      .finally(() => setRunning(false));
  };

  return (
    <div>
      <PageHeader
        title="Collections center"
        description="Overdue receivables, dunning, promises to pay, and dispute tracking"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Receivables', to: '/finance/receivables' },
          { label: 'Collections' },
        ]}
        actions={
          canRunDunning ? (
            <Button variant="primary" onClick={runDunning} disabled={running}>
              {running ? 'Running…' : 'Run dunning'}
            </Button>
          ) : undefined
        }
      />

      {center && (
        <>
          <KpiGrid className="mb-6">
            <StatCard title="Total overdue" value={fmt(center.totalOverdue)} />
            <StatCard title="Open receivables" value={fmt(center.totalOpenAr)} />
            <StatCard title="Overdue invoices" value={center.overdueInvoiceCount} />
            <StatCard title="Open promises" value={center.openPromiseCount} />
            <StatCard title="Open disputes" value={center.openDisputeCount} />
          </KpiGrid>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="card p-4 lg:col-span-2">
              <h3 className="font-semibold mb-3">Collection queue</h3>
              {queue.length === 0 ? (
                <p className="text-sm text-slate-500">No overdue receivables — great job!</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="pb-2">Customer</th>
                        <th className="pb-2">Amount</th>
                        <th className="pb-2">Days overdue</th>
                        <th className="pb-2">Promise</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedQueue.map((item) => (
                        <tr key={item.accountsReceivableId} className="border-t">
                          <td className="py-2">#{item.customerId}</td>
                          <td className="py-2 tabular-nums">{fmt(item.openAmount)}</td>
                          <td className="py-2">{item.daysOverdue}</td>
                          <td className="py-2">{item.promiseDate ?? '—'}</td>
                          <td className="py-2">
                            <Link to="/finance/invoices" className="text-primary-600 text-xs hover:underline">
                              Invoice →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <TablePagination {...queuePaginationProps} hasRows={queue.length > 0} />
                </div>
              )}
            </div>

            <div className="card p-4">
              <h3 className="font-semibold mb-3">Dunning levels</h3>
              <ul className="space-y-2 text-sm">
                {center.dunningLevels.map((l) => (
                  <li key={l.levelNumber} className="rounded border p-2">
                    <div className="font-medium">Level {l.levelNumber}</div>
                    <div className="text-slate-500">After {l.daysOverdue} days overdue</div>
                  </li>
                ))}
              </ul>
              <Link to="/finance/workflows" className="inline-block mt-3 text-xs text-primary-600 hover:underline">
                Configure workflows →
              </Link>
            </div>
          </div>
        </>
      )}

      <p className="mt-4 text-xs text-slate-500">
        Vendor self-service:{' '}
        <Link to="/vendor-portal/login" className="text-primary-600 hover:underline">
          Open vendor portal
        </Link>
      </p>
    </div>
  );
}
